import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { DocumentQuotaDisplay } from "../../components/documents/DocumentQuotaDisplay";
import { MiddleTruncate } from "../../components/common/MiddleTruncate";
import { takeDroppedFiles } from "../../features/documents/fileDrop";
import { usePreventWindowFileDrop } from "../../features/documents/hooks/usePreventWindowFileDrop";
import {
  useBulkUpload,
  useCollections,
  type BulkFileEntry,
} from "../../features/documents/hooks";
import { formatFileSize } from "../../features/documents/hooks/useBulkUpload";
import { useSupportedFormats } from "../../features/documents/hooks/useSupportedFormats";
import { describeSizeLimits } from "../../features/documents/utils/fileType";
import { trpc } from "../../lib/api/trpc";

function StatusBadge({ status }: { status: BulkFileEntry["status"] }) {
  const styles = {
    pending: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
    uploading: "bg-accent-tint text-accent",
    complete:
      "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    duplicate:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    error: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[status]}`}
    >
      {status}
    </span>
  );
}

type MatchingStrategiesResult = {
  strategies?: Array<{ id: string; name: string }>;
};

type StrategyOption = {
  id: string;
  name: string;
};

function FileQueueRow({
  entry,
  onRemove,
  onStrategyChange,
  onForceUpload,
  strategies,
  isUploading,
}: {
  entry: BulkFileEntry;
  onRemove: () => void;
  onStrategyChange: (strategyId: string) => void;
  onForceUpload: () => void;
  strategies: StrategyOption[];
  isUploading: boolean;
}) {
  const canRemove =
    !isUploading &&
    (entry.status === "pending" ||
      entry.status === "error" ||
      entry.status === "duplicate");
  const needsStrategySelection =
    strategies.length > 1 && !entry.strategyId && entry.status === "pending";
  const autoSelected = strategies.length === 1 && entry.status === "pending";

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="min-w-0">
            <MiddleTruncate
              text={entry.file.name}
              className="text-sm font-medium text-gray-900 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {formatFileSize(entry.file.size)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
          {entry.status === "complete" && entry.documentId && (
            <Link
              to={`/documents/${entry.documentId}`}
              className="text-xs text-accent hover:underline"
            >
              View
            </Link>
          )}
          <StatusBadge status={entry.status} />
          {entry.status === "complete" && (
            <CheckCircleIcon className="h-5 w-5 text-green-500" />
          )}
          {canRemove && (
            <button
              onClick={onRemove}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Strategy selection */}
      {entry.status === "pending" && strategies.length > 0 && (
        <div className="mt-2">
          {autoSelected ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Strategy: {strategies[0].name}
            </p>
          ) : strategies.length > 1 ? (
            <select
              value={entry.strategyId || ""}
              onChange={(e) => onStrategyChange(e.target.value)}
              className={`w-full px-2 py-1 border rounded text-xs bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white ${
                needsStrategySelection
                  ? "border-amber-400 dark:border-amber-500"
                  : "border-gray-300 dark:border-gray-600"
              }`}
            >
              <option value="">Select extraction strategy...</option>
              {strategies.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          ) : null}
        </div>
      )}

      {/* Progress bar */}
      {entry.status === "uploading" && (
        <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
          <div
            className="bg-accent h-1.5 rounded-full transition-all duration-200"
            style={{ width: `${entry.progress}%` }}
          />
        </div>
      )}

      {/* Warning */}
      {entry.warning && entry.status === "pending" && (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <ExclamationTriangleIcon className="h-3 w-3" />
          {entry.warning}
        </p>
      )}

      {/* Duplicate — parked for a user decision, not a failure (#158) */}
      {entry.status === "duplicate" && entry.duplicate && (
        <div className="mt-1 flex items-center justify-between gap-2">
          <p className="text-xs text-amber-600 dark:text-amber-400 min-w-0">
            {entry.duplicate.matchType === "content"
              ? `This exact document is already uploaded as "${entry.duplicate.filename}"`
              : `A document named "${entry.duplicate.filename}" already exists`}
            {entry.duplicate.collections.length > 0 &&
              ` (${entry.duplicate.collections.join(", ")})`}
          </p>
          <button
            onClick={onForceUpload}
            disabled={isUploading}
            className="flex-shrink-0 text-xs font-medium text-accent hover:underline disabled:opacity-50"
          >
            Upload anyway
          </button>
        </div>
      )}

      {/* Error */}
      {entry.error && (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
          {entry.error}
        </p>
      )}
    </div>
  );
}

function RateLimitBanner({ resumeAt }: { resumeAt: number }) {
  const [secondsLeft, setSecondsLeft] = useState(() =>
    Math.max(0, Math.ceil((resumeAt - Date.now()) / 1000)),
  );

  useEffect(() => {
    const remaining = () =>
      Math.max(0, Math.ceil((resumeAt - Date.now()) / 1000));
    setSecondsLeft(remaining());
    const id = setInterval(() => setSecondsLeft(remaining()), 1000);
    return () => clearInterval(id);
  }, [resumeAt]);

  return (
    <div className="border rounded-lg p-3 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 flex items-center">
      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2 flex-shrink-0" />
      <p className="text-sm text-yellow-800 dark:text-yellow-400">
        Upload limit reached — resuming automatically in {secondsLeft}s. Your
        remaining files will continue uploading.
      </p>
    </div>
  );
}

function UploadSummary({
  summary,
}: {
  summary: { succeeded: number; failed: number; duplicates: number };
}) {
  const allSuccess = summary.failed === 0;
  const allFailed = summary.succeeded === 0 && summary.failed > 0;

  const bgClass = allSuccess
    ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
    : allFailed
      ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
      : "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800";

  const textClass = allSuccess
    ? "text-green-800 dark:text-green-400"
    : allFailed
      ? "text-red-800 dark:text-red-400"
      : "text-yellow-800 dark:text-yellow-400";

  return (
    <div className={`border rounded-lg p-4 ${bgClass}`}>
      <p className={`text-sm font-medium ${textClass}`}>
        {allSuccess
          ? `${summary.succeeded} document${summary.succeeded !== 1 ? "s" : ""} uploaded successfully`
          : allFailed
            ? `All ${summary.failed} upload${summary.failed !== 1 ? "s" : ""} failed`
            : `${summary.succeeded} uploaded, ${summary.failed} failed`}
        {summary.duplicates > 0 &&
          ` · ${summary.duplicates} duplicate${summary.duplicates !== 1 ? "s" : ""} awaiting your decision`}
      </p>
      <Link
        to="/documents"
        className="mt-2 inline-block text-sm text-accent hover:underline"
      >
        Go to Documents
      </Link>
    </div>
  );
}

/**
 * Bulk document upload page
 * Two-column layout: file queue + drop zone on left, quota + instructions on right
 */
export function DocumentUploadPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [dragActive, setDragActive] = useState(false);
  // Pre-selected when the user came from a collection view (?collection=<id>)
  const [collectionId, setCollectionId] = useState(
    () => searchParams.get("collection") ?? "",
  );
  const [showFailedOnly, setShowFailedOnly] = useState(false);
  // Auto-enabling the failed filter must not fight the user: once they touch
  // the toggle during a batch, the batch's completion leaves it alone.
  const failedFilterTouchedRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    files,
    isUploading,
    pausedUntil,
    summary,
    addFiles,
    removeFile,
    setFileStrategy,
    clearAll,
    startUpload,
    forceUpload,
    pendingCount,
  } = useBulkUpload();
  const { acceptString, formatsLabel, formats, maxFileSizeMB } =
    useSupportedFormats();
  const { collections } = useCollections();
  usePreventWindowFileDrop();

  // Files dropped on the Documents page arrive via the in-memory stash.
  // take() clears it, so a strict-mode double-run finds it empty.
  useEffect(() => {
    const dropped = takeDroppedFiles();
    if (dropped.length > 0) {
      addFiles(dropped);
    }
  }, [addFiles]);

  // A stale/foreign ?collection= id must not reach the API (it would 400
  // every file) — only ids present in the org's collection list are sent.
  const selectedCollectionId = collections.some((c) => c.id === collectionId)
    ? collectionId
    : undefined;

  // Matching strategies per supported MIME type. The MIME list comes from the
  // backend parser registry (formats), so a new format gets a strategy picker
  // without touching this page. trpc.useQueries batches into one request.
  //
  // The trpc client type is currently unusable (a router name collides with a
  // built-in, see typecheck.sh), which is why the rest of this file uses
  // @ts-ignore. Typing the one call we need via a minimal interface keeps the
  // expected shape explicit instead of suppressing errors.
  const { useQueries: useStrategyQueries } = trpc as unknown as {
    useQueries: (
      build: (t: {
        organization: {
          getMatchingStrategies: (
            input: { mimeType: string },
            opts: { staleTime: number },
          ) => unknown;
        };
      }) => unknown[],
    ) => Array<{ data?: MatchingStrategiesResult }>;
  };
  const strategyQueries = useStrategyQueries((t) =>
    formats.map((f) =>
      t.organization.getMatchingStrategies(
        { mimeType: f.mime },
        { staleTime: 1000 * 60 * 5 },
      ),
    ),
  );
  const strategyQueryData = strategyQueries.map((q) => q.data);
  const strategiesByMime = useMemo(() => {
    const byMime: Record<string, StrategyOption[]> = {};
    formats.forEach((f, i) => {
      byMime[f.mime] =
        strategyQueryData[i]?.strategies?.map((s) => ({
          id: s.id,
          name: s.name,
        })) ?? [];
    });
    return byMime;
    // strategyQueryData is a fresh array each render; key the memo on its items.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formats, ...strategyQueryData]);

  const getStrategiesForMimeType = useCallback(
    (mimeType: string | null): StrategyOption[] =>
      (mimeType && strategiesByMime[mimeType]) || [],
    [strategiesByMime],
  );

  // Auto-assign strategy for files with exactly one match
  useEffect(() => {
    for (const f of files) {
      if (f.status === "pending" && !f.strategyId) {
        const strats = getStrategiesForMimeType(f.mimeType);
        if (strats.length === 1) {
          setFileStrategy(f.id, strats[0].id);
        }
      }
    }
  }, [files, getStrategiesForMimeType, setFileStrategy]);

  const failedCount = files.filter((f) => f.status === "error").length;
  const displayedFiles =
    showFailedOnly && failedCount > 0
      ? files.filter((f) => f.status === "error")
      : files;

  // Land the user on the failures when a batch finishes with any — in a
  // 100+ file batch that's the whole point of the queue.
  useEffect(() => {
    if (summary && summary.failed > 0 && !failedFilterTouchedRef.current) {
      setShowFailedOnly(true);
    }
  }, [summary]);

  // Check if any pending files need strategy selection
  const needsStrategySelection = files.some((f) => {
    if (f.status !== "pending") return false;
    const strategies = getStrategiesForMimeType(f.mimeType);
    return strategies.length > 1 && !f.strategyId;
  });

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // New files must be visible immediately, so incoming batches clear the
  // failed-only filter.
  const resetFailedFilter = () => {
    setShowFailedOnly(false);
    failedFilterTouchedRef.current = false;
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      resetFailedFilter();
      addFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      resetFailedFilter();
      addFiles(e.target.files);
      // Reset input so the same files can be re-selected
      e.target.value = "";
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-darkmode-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/documents")}
            className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Documents
          </button>
          <h1 className="font-serif text-3xl font-normal tracking-[-0.005em] text-[var(--text)]">
            Upload Documents
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Upload PDF documents to be processed and indexed for search
          </p>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left column: Upload area */}
          <div className="space-y-6">
            {/* Drop zone */}
            <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Select Files
              </h3>

              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={!isUploading ? handleButtonClick : undefined}
                className={`
                  border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                  ${dragActive ? "border-accent bg-accent-tint" : "border-gray-300 dark:border-gray-600"}
                  ${isUploading ? "opacity-50 cursor-not-allowed" : "hover:border-accent hover:bg-gray-50 dark:hover:bg-darkmode-700"}
                `}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept={acceptString}
                  multiple
                  onChange={handleChange}
                  disabled={isUploading}
                />

                <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Drag and drop your documents here
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-4">
                  or
                </p>
                <button
                  type="button"
                  disabled={isUploading}
                  className="px-4 py-2 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-strong disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Browse files
                </button>
              </div>

              <div className="mt-4 text-xs text-gray-600 dark:text-gray-400">
                <p>
                  Supported formats: {formatsLabel}.{" "}
                  {describeSizeLimits(formats, maxFileSizeMB)}
                </p>
              </div>
            </div>

            {/* File queue */}
            {files.length > 0 && (
              <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    File Queue ({files.length})
                  </h3>
                  <div className="flex items-center gap-3">
                    {failedCount > 0 && (
                      <button
                        onClick={() => {
                          failedFilterTouchedRef.current = true;
                          setShowFailedOnly((v) => !v);
                        }}
                        className={`px-2 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                          showFailedOnly
                            ? "bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800"
                            : "bg-transparent text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:text-red-600 hover:border-red-300"
                        }`}
                      >
                        {showFailedOnly
                          ? `Showing ${failedCount} failed — show all`
                          : `Failed only (${failedCount})`}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowFailedOnly(false);
                        failedFilterTouchedRef.current = false;
                        clearAll();
                      }}
                      disabled={isUploading}
                      className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Clear all
                    </button>
                  </div>
                </div>

                {pausedUntil !== null && (
                  <div className="mb-4">
                    <RateLimitBanner resumeAt={pausedUntil} />
                  </div>
                )}

                <div className="space-y-2">
                  {displayedFiles.map((entry) => (
                    <FileQueueRow
                      key={entry.id}
                      entry={entry}
                      onRemove={() => removeFile(entry.id)}
                      onForceUpload={() => void forceUpload(entry.id)}
                      onStrategyChange={(sid) => setFileStrategy(entry.id, sid)}
                      strategies={getStrategiesForMimeType(entry.mimeType)}
                      isUploading={isUploading}
                    />
                  ))}
                </div>

                {/* Collection selection — applies to the whole batch */}
                {collections.length > 0 && (
                  <div className="mt-4 flex items-center gap-2">
                    <label
                      htmlFor="upload-collection"
                      className="text-sm text-gray-600 dark:text-gray-400 flex-shrink-0"
                    >
                      Add to collection
                    </label>
                    <select
                      id="upload-collection"
                      value={selectedCollectionId ?? ""}
                      onChange={(e) => setCollectionId(e.target.value)}
                      disabled={isUploading}
                      className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white disabled:opacity-50"
                    >
                      <option value="">No collection</option>
                      {collections.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Action buttons */}
                <div className="mt-4 flex items-center gap-3">
                  <button
                    onClick={() => {
                      resetFailedFilter();
                      void startUpload(selectedCollectionId);
                    }}
                    disabled={
                      pendingCount === 0 ||
                      isUploading ||
                      needsStrategySelection
                    }
                    className="px-4 py-2 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-strong disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isUploading
                      ? "Uploading..."
                      : `Upload ${pendingCount} file${pendingCount !== 1 ? "s" : ""}`}
                  </button>
                </div>
                {needsStrategySelection && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                    Please select an extraction strategy for all files before
                    uploading
                  </p>
                )}

                {/* Summary */}
                {summary && (
                  <div className="mt-4">
                    <UploadSummary summary={summary} />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right column: Quota and Instructions */}
          <div className="space-y-6">
            <DocumentQuotaDisplay />

            <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                How it works
              </h3>
              <ol className="space-y-4 text-sm text-gray-600 dark:text-gray-400">
                <li className="flex">
                  <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-accent-tint text-accent font-medium mr-3">
                    1
                  </span>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Upload Documents
                    </p>
                    <p className="mt-1">
                      Your documents are securely uploaded and stored
                    </p>
                  </div>
                </li>
                <li className="flex">
                  <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-accent-tint text-accent font-medium mr-3">
                    2
                  </span>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Content Extraction
                    </p>
                    <p className="mt-1">
                      Text is extracted and intelligently chunked for processing
                    </p>
                  </div>
                </li>
                <li className="flex">
                  <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-accent-tint text-accent font-medium mr-3">
                    3
                  </span>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Human Validation
                    </p>
                    <p className="mt-1">
                      Chunks are validated for accuracy to ensure zero
                      hallucinations
                    </p>
                  </div>
                </li>
                <li className="flex">
                  <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-accent-tint text-accent font-medium mr-3">
                    4
                  </span>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Ready for Search
                    </p>
                    <p className="mt-1">
                      Validated content is indexed and becomes searchable and
                      chat-ready
                    </p>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
